import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Search, Plus, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ImageUpload from '../../components/ImageUpload';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTemples = () => {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTemple, setEditingTemple] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    image_url: '',
    category: 'other',
    state: '',
    pujas_available: []
  });

  useEffect(() => {
    fetchTemples();
  }, []);

  const fetchTemples = async () => {
    try {
      const response = await axios.get(`${API}/temples`);
      setTemples(response.data);
    } catch (error) {
      console.error('Error fetching temples:', error);
      toast.error('Failed to load temples');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemple) {
        await axios.put(`${API}/temples/${editingTemple.id}`, formData, { withCredentials: true });
        toast.success('Temple updated successfully');
      } else {
        await axios.post(`${API}/temples`, formData, { withCredentials: true });
        toast.success('Temple created successfully');
      }
      setShowForm(false);
      setEditingTemple(null);
      resetForm();
      fetchTemples();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save temple');
    }
  };

  const handleEdit = (temple) => {
    setEditingTemple(temple);
    setFormData({
      name: temple.name,
      location: temple.location,
      description: temple.description,
      image_url: temple.image_url,
      category: temple.category,
      state: temple.state,
      pujas_available: temple.pujas_available || []
    });
    setShowForm(true);
  };

  const handleDelete = async (templeId) => {
    if (!window.confirm('Are you sure you want to delete this temple?')) return;
    try {
      await axios.delete(`${API}/temples/${templeId}`, { withCredentials: true });
      toast.success('Temple deleted');
      fetchTemples();
    } catch (error) {
      toast.error('Failed to delete temple');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      description: '',
      image_url: '',
      category: 'other',
      state: '',
      pujas_available: []
    });
  };

  const filteredTemples = temples.filter(temple =>
    temple.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    temple.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [
    { value: 'char_dham', label: 'Char Dham' },
    { value: 'panch_kedar', label: 'Panch Kedar' },
    { value: 'shakti_peeth', label: 'Shakti Peeth' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search temples..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="temple-search"
          />
        </div>
        <Button
          onClick={() => { setShowForm(true); setEditingTemple(null); resetForm(); }}
          className="btn-saffron"
          data-testid="add-temple-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Temple
        </Button>
      </div>

      {/* Temples Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemples.map((temple) => (
          <motion.div
            key={temple.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            data-testid={`temple-card-${temple.id}`}
          >
            <div className="h-40 overflow-hidden">
              <img
                src={temple.image_url}
                alt={temple.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-heading font-bold text-[#1A202C]">{temple.name}</h3>
                  <p className="text-xs text-gray-500 flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {temple.location}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                  temple.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {temple.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">{temple.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">{temple.category.replace('_', ' ')}</span>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(temple)}
                    data-testid={`edit-temple-${temple.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(temple.id)}
                    data-testid={`delete-temple-${temple.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredTemples.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No temples found</p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold text-[#1A202C]">
                {editingTemple ? 'Edit Temple' : 'Add New Temple'}
              </h3>
              <button
                onClick={() => { setShowForm(false); setEditingTemple(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Temple Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="temple-name-input"
                  />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    data-testid="temple-location-input"
                  />
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <p className="text-xs text-gray-400 mb-1">Write a detailed description about this temple's history, significance, and spiritual importance</p>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg p-3 min-h-[180px] resize-y"
                  required
                  data-testid="temple-description-input"
                  placeholder="Describe the temple's history, significance, deities worshipped, best time to visit, and spiritual importance..."
                />
              </div>

              <div>
                <ImageUpload
                  label="Temple Image *"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg p-3"
                    data-testid="temple-category-select"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>State *</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                    data-testid="temple-state-input"
                  />
                </div>
              </div>

              <div>
                <Label>Available Pujas (comma separated)</Label>
                <Input
                  value={formData.pujas_available.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    pujas_available: e.target.value.split(',').map(p => p.trim()).filter(Boolean)
                  })}
                  placeholder="Rudrabhishek, Maha Mrityunjaya Jaap"
                  data-testid="temple-pujas-input"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); setEditingTemple(null); }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="btn-saffron" data-testid="save-temple-btn">
                  {editingTemple ? 'Update Temple' : 'Create Temple'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminTemples;
